import { html, TemplateResult, CSSResultGroup } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';
import componentStyles from './tufts-video-modal.css.lit';
import { OutlineElement } from '../../base/outline-element/outline-element';
import { ifDefined } from 'lit/directives/if-defined.js';
import { SlotNGController } from '../../controllers/slot-ng-controller';

export interface TuftsVideoModalInterface extends HTMLElement {
  isOpen: boolean;
  open: () => void;
  close: () => void;
}

/**
 * The Tufts Modal component
 * @element tufts-video-modal
 *
 * @slot video-image- The trigger for the video modal
 * @slot video-summary - The content underneath the video
 * @slot accessibility-description - The accessibility description which is used by screen readers.
 *
 * @attr video-id
 * @attr full-image
 * @attr video-provider
 * @attr video-after
 */

@customElement('tufts-video-modal')
export class TuftsVideoModal
  extends OutlineElement
  implements TuftsVideoModalInterface
{
  ngSlot = new SlotNGController(this);

  static styles: CSSResultGroup = [componentStyles];

  @property({ type: String, reflect: true, attribute: 'video-id' })
  videoID: string;
  @property({ type: String, reflect: true, attribute: 'full-image' })
  fullImage: string;
  @property({ type: String, reflect: true, attribute: 'video-provider' })
  videoProvider: string;
  @property({ type: String, reflect: true, attribute: 'video-after' })
  videoAfter: string;

  @property({ attribute: false })
  isOpen = false;

  /**
   * If we force the user to take an action, the consumer must provide a way to close the modal on their own.
   */
  @property({ type: Boolean })
  shouldForceAction = false;

  @query('iframe')
  private iFrame!: HTMLElement;

  _pauseVideo = () => {
    this.shadowRoot
      ?.querySelector('iframe')
      ?.contentWindow?.postMessage(
        '{"event":"command","func":"' + 'pauseVideo' + '","args":""}',
        '*'
      );
  };

  _closeAll() {
    if (this.iFrame) {
      this._pauseVideo();
      this.close();
    } else {
      this.close();
    }
  }

  render(): TemplateResult {
    return html`
      <section class="${this.fullImage}">
        <button
          class="trigger"
          @click="${this.open}"
          aria-label="Play Video"
          focusable="true"
          @keydown="${this._handleTriggerKeydown}"
        >
          <div class="video-wrapper">
            ${this.playIconTemplate()}
            ${this.ngSlot.conditionalNGSlot('video-image')}
          </div>
        </button>
        ${this.ngSlot.conditionalNGSlot('video-summary')}
      </section>
      ${this._modalTemplate()}
    `;
  }

  updated() {
    // If a page resize occurs while a video is playing, restore the scrolling of the page (the component has been rerendered and the video has been closed).
    document.body.style.removeProperty('overflow');
  }

  @state()
  _hasAccessibilityDescriptionSlot: boolean;

  connectedCallback() {
    super.connectedCallback();
    this._handleSlotChange();
  }

  private _handleSlotChange(): void {
    this._hasAccessibilityDescriptionSlot =
      this.querySelector('[slot="accessibility-description"]') !== null;
  }

  private _modalTemplate(): TemplateResult {
    let template = html``;

    if (this.isOpen) {
      template = html`
        <section modal tabindex="-1">
          <div
            class="modal"
            tabindex="-1"
            @click="${this._handleOverlayClick}"
            @keydown="${this._handleOverlayKeydown}"
          >
            <div
              class="modal-container"
              role="dialog"
              aria-modal="true"
              aria-describedby="${ifDefined(
                this._hasAccessibilityDescriptionSlot
                  ? 'accessibility-description'
                  : undefined
              )}"
            >
              <div class="modal-content">
                <button
                  focusable="true"
                  class="close"
                  aria-label="Close modal"
                  @click="${this.close}"
                  @keydown="${this._handleCloseKeydown}"
                >
                  ${this.closeSVGTemplate()}
                </button>
                <div class="video">
                  <iframe
                    class="video-iframe"
                    width="100%"
                    height="100%"
                    src="${this.videoProvider}${this.videoID}${this.videoAfter}"
                    title="Video ${this.videoID}"
                    frameborder="0"
                    allow="autoplay; picture-in-picture; fullscreen"
                    allowfullscreen="true"
                    allowAutoplay="true"
                  ></iframe>
                </div>
              </div>
            </div>
          </div>
          <slot
            id="accessibility-description"
            name="accessibility-description"
            @slotchange="${this._handleSlotChange}"
          ></slot>
        </section>
      `;
    }

    return template;
  }

  async open(): Promise<void> {
    if (!this.isOpen) {
      this.isOpen = true;

      await this.updateComplete;

      const closeButton: HTMLElement | null | undefined =
        this.shadowRoot?.querySelector('.close');

      if (closeButton) {
        closeButton.focus();
      }

      // disable page scrolling functionality while modal is open
      document.body.style.overflow = 'hidden';

      this._trapFocus();

      this.dispatchEvent(new CustomEvent('opened'));
    }
  }

  async close(): Promise<void> {
    if (this.isOpen) {
      this.isOpen = false;

      await this.updateComplete;

      // restore page scrolling
      document.body.style.removeProperty('overflow');

      this.dispatchEvent(new CustomEvent('closed'));

      this.triggerElement.focus();
    }
  }

  @query('.trigger')
  private triggerElement!: HTMLDivElement;

  private _handleTriggerKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      // This prevents a focused element from also triggering.
      // For example, the modal opens and the "accept" button is focused and then triggered and the modal closes.
      event.preventDefault();

      this.open();
    }
  }

  private _handleOverlayClick(event: MouseEvent): void {
    // Only trigger if we click directly on the event that wants to receive the click.
    if (event.target === event.currentTarget) {
      this.close();
    }
  }

  private _handleOverlayKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      this.close();
    }
  }

  // For some reason on the `Docs` tab of Storybook, the `click` event for the close button doesn't work with the `Enter` key without also watching the `keyup` event. This isn't the case on the `Canvas` tab.
  private _handleCloseKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      this.close();
    }
  }

  @property({ type: String })
  elementToFocusSelector?: string | undefined;

  private _trapFocus(): void {
    const modalElement = this.shadowRoot?.querySelector('.modal');

    // Define a selector that matches elements that can be focused by default
    const defaultFocusableSelector =
      'a[href], button, input, select, textarea, [tabindex]';

    // Query all the elements that match the selector in the document
    const defaultFocusableElements = modalElement?.querySelectorAll(
      defaultFocusableSelector
    );

    const firstFocusableElement =
      defaultFocusableElements && (defaultFocusableElements[0] as HTMLElement);
    const lastFocusableSlottedElement =
      defaultFocusableElements &&
      (defaultFocusableElements[
        defaultFocusableElements.length - 1
      ] as HTMLElement);

    if (firstFocusableElement) {
      const lastFocusableElement =
        lastFocusableSlottedElement ?? firstFocusableElement;

      if (lastFocusableElement) {
        lastFocusableElement.addEventListener(
          'keydown',
          (event: KeyboardEvent) => {
            if (event.key === 'Tab' && event.shiftKey === false) {
              event.preventDefault();
              firstFocusableElement.focus();
            }
          }
        );
      }

      if (firstFocusableElement) {
        firstFocusableElement.addEventListener(
          'keydown',
          (event: KeyboardEvent) => {
            if (event.key === 'Tab' && event.shiftKey) {
              event.preventDefault();
              if (lastFocusableElement) {
                lastFocusableElement.focus();
              }
            }
          }
        );
      }
    }
  }

  closeSVGTemplate(): TemplateResult | null {
    return html`<svg
      min-width="60"
      min-height="60"
      viewBox="0 0 60 60"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
    <circle cx="30" cy="30" r="29.5" />
    <path
      d="M21.4142 20.7071L39.0919 38.3848L38.3847 39.0919L20.7071 21.4142L21.4142 20.7071Z"
    />
    <path
      d="M20.7072 38.3848L38.3849 20.7071L39.092 21.4142L21.4143 39.0919L20.7072 38.3848Z"
    >
    </svg>`;
  }

  playIconTemplate(): TemplateResult | null {
    return html`
      <svg
        width="116"
        height="116"
        viewBox="0 0 116 116"
        fill="none"
        class="play-button"
        xmlns="http://www.w3.org/2000/svg"
      >
        <circle cx="50" cy="48" r="32" />
        <path
          fill-rule="evenodd"
          clip-rule="evenodd"
          d="M61 47.5L43 35.8087L43 59.1913L61 47.5ZM57.3283 47.5L45 39.4925L45 55.5075L57.3283 47.5Z"
          ;
        />
        <defs>
          <filter
            id="filter0_dd_2438_2641"
            x="0"
            y="0"
            width="116"
            height="116"
            filterUnits="userSpaceOnUse"
            color-interpolation-filters="sRGB"
          >
            <feFlood flood-opacity="0" result="BackgroundImageFix" />
            <feColorMatrix
              in="SourceAlpha"
              type="matrix"
              values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
              result="hardAlpha"
            />
            <feOffset dx="8" dy="10" />
            <feGaussianBlur stdDeviation="13" />
            <feComposite in2="hardAlpha" operator="out" />
            <feColorMatrix
              type="matrix"
              values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.19 0"
            />
            <feBlend
              mode="normal"
              in2="BackgroundImageFix"
              result="effect1_dropShadow_2438_2641"
            />
            <feColorMatrix
              in="SourceAlpha"
              type="matrix"
              values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
              result="hardAlpha"
            />
            <feOffset dx="3" dy="3" />
            <feGaussianBlur stdDeviation="5.5" />
            <feComposite in2="hardAlpha" operator="out" />
            <feColorMatrix
              type="matrix"
              values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.05 0"
            />
            <feBlend
              mode="normal"
              in2="effect1_dropShadow_2438_2641"
              result="effect2_dropShadow_2438_2641"
            />
            <feBlend
              mode="normal"
              in="SourceGraphic"
              in2="effect2_dropShadow_2438_2641"
              result="shape"
            />
          </filter>
        </defs>
      </svg>
    `;
  }
}
declare global {
  interface HTMLElementTagNameMap {
    'tufts-video-modal': TuftsVideoModal;
  }
}
